import { Link, useLocation, useParams } from 'react-router-dom';
import { useTheme } from '@mui/material/styles';
import { Grid, Typography } from '@mui/material';
import MuiBreadcrumbs from '@mui/material/Breadcrumbs';
import MainCard from 'components/ui/Card/MainCard';
import ArrowForwardIosIcon from '@mui/icons-material/ArrowForwardIos';
import { Box, styled } from '@mui/system';
import { FormattedMessage } from 'react-intl';
import useTranslationKeyExist from 'hooks/useTranslationKeyExist';
import { useGetApiProjectResourceQuery } from 'store/userOrg/enhancedUserOrgApi';
import { useEffect, useState } from 'react';
import useCheckPermission from 'hooks/useCheckPermission';
import { permissions } from 'constants/permissions';
import { useSearchForDatasetsByDatasetIdMetaSearchDatasetIdGetQuery } from 'store/metadata/enhancedMetadataApi';
import { APP_DEFAULT_PATH } from 'config';

const StyledNoUnderlineLink = styled(Link)(({ theme }) => ({
    textDecoration: 'none',
    color: theme.palette.primary.main,
}));

const Breadcrumbs = ({}) => {
    const checkPerms = useCheckPermission();
    const { dataSetId, resourceId } = useParams();
    const [skipFetchResources, setSkipFetchResources] = useState(true);

    useEffect(() => {
        checkPerms(permissions.resList, '') && setSkipFetchResources(false);
    }, []);

    const { data: dataset } = useSearchForDatasetsByDatasetIdMetaSearchDatasetIdGetQuery(
        {
            datasetId: dataSetId ?? '',
        },
        { skip: !dataSetId }
    );
    const { data: resources } = useGetApiProjectResourceQuery({}, { skip: skipFetchResources });

    const resourceName = resources?.find(resource => resource.Id === resourceId)?.Name;
    const datasetTitle = dataset?.lexis?.title;

    const theme = useTheme();
    const location = useLocation();
    const keyExists = useTranslationKeyExist();
    const iconSX = {
        marginRight: theme.spacing(0.75),
        marginTop: `-${theme.spacing(0.25)}`,
        width: '0.5rem',
        height: '0.5rem',
        color: theme.palette.secondary.main,
    };

    let items = location.pathname.split('/');
    items = [...items].filter(e => String(e).trim());
    const params = Object.values(useParams());

    const getAllBefore = currentIndex => {
        return items.slice(0, currentIndex);
    };

    return (
        <MainCard sx={{ mb: 0 }} shadow='none'>
            <Grid container direction='row' justifyContent='flex-start' alignItems='flex-start' spacing={1}>
                <Grid item>
                    <MuiBreadcrumbs aria-label='breadcrumb' maxItems={8} separator={false}>
                        {location.pathname !== APP_DEFAULT_PATH ? (
                            <StyledNoUnderlineLink to={APP_DEFAULT_PATH}>
                                <Typography variant='h6' fontWeight='bold'>
                                    Home
                                </Typography>
                            </StyledNoUnderlineLink>
                        ) : (
                            <Typography variant='h6'>Home</Typography>
                        )}
                        {items.map((item, index) => {
                            const decodedItem = (() => {
                                try {
                                    return decodeURIComponent(item);
                                } catch {
                                    return item;
                                }
                            })();
                            const isLast = index === items.length - 1;
                            const isParam = item !== '' && params.includes(decodedItem);
                            const isDatasetOrResource = dataSetId === item || resourceId === item;
                            const displayText = isDatasetOrResource
                                ? datasetTitle ?? resourceName ?? decodedItem
                                : decodedItem;

                            return (
                                <Box key={`${item}-${index}`}>
                                    {isParam || isLast ? (
                                        <Typography variant='h6'>
                                            <ArrowForwardIosIcon sx={iconSX} />
                                            {keyExists(decodedItem) ? (
                                                <FormattedMessage id={decodedItem} />
                                            ) : keyExists(item) ? (
                                                <FormattedMessage id={item} />
                                            ) : (
                                                displayText
                                            )}
                                        </Typography>
                                    ) : (
                                        <StyledNoUnderlineLink to={`${getAllBefore(index).join('/')}/${item}`}>
                                            <Typography variant='h6' fontWeight='bold'>
                                                <ArrowForwardIosIcon sx={iconSX} />
                                                {keyExists(decodedItem) ? (
                                                    <FormattedMessage id={decodedItem} />
                                                ) : keyExists(item) ? (
                                                    <FormattedMessage id={item} />
                                                ) : (
                                                    displayText
                                                )}
                                            </Typography>
                                        </StyledNoUnderlineLink>
                                    )}
                                </Box>
                            );
                        })}
                    </MuiBreadcrumbs>
                </Grid>
            </Grid>
        </MainCard>
    );
};

export default Breadcrumbs;
